import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthGuard } from './guards/AuthGuard';
import { GuestGuard } from './guards/GuestGuard';
import { PlatformAdminGuard } from './guards/PlatformAdminGuard';
import AppWrapper from '../components/app-wrapper';
import { AuthLayout } from '../components/auth-layout';
import { DashboardRedirect } from '../components/portal/dashboard-redirect';
import { SettingsRedirect } from '../components/portal/settings-redirect';
import { PortalAdminGuard } from '../components/portal/portal-admin-guard';

function AuthPageLayout() {
  return (
    <AuthLayout>
      <Outlet />
    </AuthLayout>
  );
}

function RegistrationPageLayout() {
  return (
    <AuthLayout wide>
      <Outlet />
    </AuthLayout>
  );
}

// Lazy load pages
import { lazy, Suspense } from 'react';

const ClinicianInvitePage = lazy(
  () => import('../app/(auth)/clinician-invite/page'),
);
const LoginPage = lazy(() => import('../app/(auth)/login/page'));
const SignupPage = lazy(() => import('../app/(auth)/signup/page'));
const ClinicianRegistrationPage = lazy(
  () => import('../app/(auth)/signup/details/page'),
);
const ForgotPasswordPage = lazy(
  () => import('../app/(auth)/forgot-password/page'),
);
const ResetPasswordPage = lazy(
  () => import('../app/(auth)/auth/reset-password-step2/page'),
);
const InvitePage = lazy(() => import('../app/(auth)/invite/page'));
const VerifyEmailPage = lazy(() => import('../app/(auth)/verify-email/page'));
const SignupSuccessPage = lazy(
  () => import('../app/(auth)/signup-success/page'),
);
const PatientSessionPage = lazy(() => import('./pages/PatientSessionPage'));

// Doctor portal lazy imports (issue #307)
const PortalLayout = lazy(() =>
  import('../app/portal/layout').then(m => ({ default: m.PortalLayout })),
);
const PortalDetailsPage = lazy(() => import('../app/portal/details/page'));
const PortalSpecialtyPage = lazy(() => import('../app/portal/specialty/page'));
const PortalPatientPinPage = lazy(
  () => import('../app/portal/patient-pin/page'),
);
const PortalLinkPage = lazy(() => import('../app/portal/link/page'));
const PortalAnalyticsPage = lazy(() => import('../app/portal/analytics/page'));
const PortalDoctorsPage = lazy(() => import('../app/portal/doctors/page'));
const PortalClinicPage = lazy(() => import('../app/portal/clinic/page'));
const PortalSecurityPage = lazy(() => import('../app/portal/security/page'));
const PortalSettingsPage = lazy(() => import('../app/portal/settings/page'));

// Platform Admin lazy imports
const PlatformAdminLayout = lazy(() =>
  import('../app/platform-admin/layout').then(m => ({
    default: m.PlatformAdminLayout,
  })),
);
const PlatformDashboardPage = lazy(
  () => import('../app/platform-admin/dashboard/page'),
);
const PlatformAnalyticsPage = lazy(
  () => import('../app/platform-admin/analytics/page'),
);
const PlatformCustomerHistoryPage = lazy(
  () => import('@/app/platform-admin/customer-history/page'),
);
const PlatformSpecialtiesPage = lazy(
  () => import('../app/platform-admin/specialties/page'),
);
const PlatformPromptsPage = lazy(
  () => import('../app/platform-admin/prompts/page'),
);
const PlatformReferencePage = lazy(
  () => import('../app/platform-admin/reference/page'),
);
const PlatformClinicsPage = lazy(
  () => import('../app/platform-admin/clinics/page'),
);
const PlatformDoctorsPage = lazy(
  () => import('../app/platform-admin/doctors/page'),
);
const PlatformUsersPage = lazy(
  () => import('../app/platform-admin/users/page'),
);
const PlatformStripePage = lazy(
  () => import('../app/platform-admin/stripe/page'),
);
const PlatformClinicDetailPage = lazy(
  () => import('../app/platform-admin/clinics/[id]/page'),
);
const SubscriptionPage = lazy(() => import('../app/subscription/page'));
const DoctorBillingPage = lazy(() => import('../app/portal/billing/page'));
const BillingSetupPage = lazy(() => import('../app/billing-setup/page'));

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

export function AppRouter() {
  return (
    <AppWrapper>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route element={<AuthPageLayout />}>
            <Route path="/clinician-invite" element={<ClinicianInvitePage />} />
          </Route>
          {/* Auth routes — only accessible when NOT logged in */}
          <Route element={<GuestGuard />}>
            <Route element={<RegistrationPageLayout />}>
              <Route
                path="/signup/details"
                element={<ClinicianRegistrationPage />}
              />
            </Route>
            <Route element={<AuthPageLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route
                path="/auth/reset-password-step2"
                element={<ResetPasswordPage />}
              />
              <Route path="/invite" element={<InvitePage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/signup-success" element={<SignupSuccessPage />} />
            </Route>
          </Route>

          {/* Authenticated routes — subscription status checked at component level */}
          <Route element={<AuthGuard />}>
            {/* Retired legacy billing page — doctor-owned billing now lives in
                the portal route, where its persona gate can be explicit. */}
            <Route
              path="/dashboard/billing"
              element={<Navigate to="/portal/billing" replace />}
            />
            <Route path="/billing-setup" element={<BillingSetupPage />} />

            {/* Doctor portal (issue #307) — one shell, tabs vary by persona */}
            <Route element={<PortalLayout />}>
              <Route path="/portal/details" element={<PortalDetailsPage />} />
              <Route
                path="/portal/specialty"
                element={<PortalSpecialtyPage />}
              />
              <Route
                path="/portal/patient-pin"
                element={<PortalPatientPinPage />}
              />
              <Route path="/portal/link" element={<PortalLinkPage />} />
              <Route
                path="/portal/analytics"
                element={<PortalAnalyticsPage />}
              />
              <Route path="/portal/doctors" element={<PortalDoctorsPage />} />
              <Route path="/portal/clinic" element={<PortalClinicPage />} />
              {/* Doctor-owned billing is deliberately outside the clinic
                  subscription guard. The page redirects clinic-channel
                  doctors back to their patient-link surface when the
                  summary endpoint returns null. */}
              <Route path="/portal/billing" element={<DoctorBillingPage />} />
              {/* Admin-only: portalAccountLinks already hides this tab from
                  a plain doctor's avatar menu, but hiding a tab only hides
                  the tab — direct feedback, 2026-08-20, 3rd round: a doctor
                  (test4.test4) could still reach /portal/subscription by
                  URL. PortalAdminGuard closes that at the route level. */}
              <Route element={<PortalAdminGuard />}>
                <Route
                  path="/portal/subscription"
                  element={<SubscriptionPage />}
                />
              </Route>
              <Route path="/portal/security" element={<PortalSecurityPage />} />
              <Route path="/portal/settings" element={<PortalSettingsPage />} />
            </Route>

            {/* Old routes, redirected to their /portal/* successors — nothing
                a doctor has bookmarked or been sent breaks (issue #307). */}
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route path="/settings" element={<SettingsRedirect />} />
            <Route
              path="/stats"
              element={<Navigate to="/portal/analytics" replace />}
            />
            <Route
              path="/subscription"
              element={<Navigate to="/portal/subscription" replace />}
            />
          </Route>

          {/* Platform Admin routes — require platformRole */}
          <Route element={<PlatformAdminGuard />}>
            <Route element={<PlatformAdminLayout />}>
              <Route
                path="/platform-admin/dashboard"
                element={<PlatformDashboardPage />}
              />
              <Route
                path="/platform-admin/analytics"
                element={<PlatformAnalyticsPage />}
              />
              <Route
                path="/platform-admin/customer-history"
                element={<PlatformCustomerHistoryPage />}
              />
              <Route
                path="/platform-admin/specialties"
                element={<PlatformSpecialtiesPage />}
              />
              <Route
                path="/platform-admin/prompts"
                element={<PlatformPromptsPage />}
              />
              <Route
                path="/platform-admin/reference"
                element={<PlatformReferencePage />}
              />
              <Route
                path="/platform-admin/clinics"
                element={<PlatformClinicsPage />}
              />
              <Route
                path="/platform-admin/clinics/:id"
                element={<PlatformClinicDetailPage />}
              />
              <Route
                path="/platform-admin/doctors"
                element={<PlatformDoctorsPage />}
              />
              <Route
                path="/platform-admin/users"
                element={<PlatformUsersPage />}
              />
              <Route
                path="/platform-admin/stripe"
                element={<PlatformStripePage />}
              />
            </Route>
          </Route>
          <Route
            path="/platform-admin"
            element={<Navigate to="/platform-admin/dashboard" replace />}
          />

          {/* Patient session — public, uses session token (not Firebase auth).
              The splat carries the Offering Path ("diabetes/review"); bare
              /dr/:username runs the doctor's default Offering (ADR 0001). */}
          <Route path="/dr/:username" element={<PatientSessionPage />} />
          <Route path="/dr/:username/*" element={<PatientSessionPage />} />

          {/* Root redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </AppWrapper>
  );
}
